import { useMemo, useState } from 'react';
import type { CategoryTags } from '../category-tags';
import { languageName, t } from '../i18n';

/** The language and country menus shared by the browse screens and Search. */
export interface TagFilter {
  language: string | null;
  country: string | null;
}

export const NO_FILTER: TagFilter = { language: null, country: null };

/** Something the menus count: a category's tags and how many items it holds. */
export interface Tagged {
  tags: CategoryTags;
  itemCount: number;
}

export function matchesFilter(tags: CategoryTags, filter: TagFilter): boolean {
  return (filter.language === null || tags.language === filter.language) && (filter.country === null || tags.country === filter.country);
}

/** The languages (or countries) present, with how many items each has, most items first. */
function options(list: Tagged[], field: keyof CategoryTags): [string, number][] {
  const counts = new Map<string, number>();
  for (const { tags, itemCount } of list) {
    const value = tags[field];
    if (value !== null) counts.set(value, (counts.get(value) ?? 0) + itemCount);
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

export interface TagFilterState {
  /** The choice in effect: a remembered value this list does not have (another provider) is ignored. */
  active: TagFilter;
  filtered: boolean;
  languages: [string, number][] | null;
  countries: [string, number][] | null;
  change: (next: TagFilter) => void;
}

/** Filter state for `tagged`, remembered per `scope` (Live TV, Movies, Series, Search). */
export function useTagFilter(scope: string, tagged: Tagged[]): TagFilterState {
  const [filter, setFilter] = useState<TagFilter>(() => readFilter(scope));
  // Each menu offers what the other filter leaves, so no choice ends in an empty list.
  const languages = useMemo(() => options(tagged.filter(({ tags }) => matchesFilter(tags, { ...filter, language: null })), 'language'), [tagged, filter]);
  const countries = useMemo(() => options(tagged.filter(({ tags }) => matchesFilter(tags, { ...filter, country: null })), 'country'), [tagged, filter]);
  // Which menus exist depends on the whole list, so a menu never vanishes when the other one narrows it.
  const hasLanguages = useMemo(() => options(tagged, 'language').length > 1, [tagged]);
  const hasCountries = useMemo(() => options(tagged, 'country').length > 1, [tagged]);
  const active: TagFilter = {
    language: filter.language !== null && languages.some(([name]) => name === filter.language) ? filter.language : null,
    country: filter.country !== null && countries.some(([name]) => name === filter.country) ? filter.country : null,
  };
  return {
    active,
    filtered: active.language !== null || active.country !== null,
    languages: hasLanguages ? languages : null,
    countries: hasCountries ? countries : null,
    change: (next) => {
      setFilter(next);
      saveFilter(scope, next);
    },
  };
}

/** True while a remembered choice exists, even before the list is known. */
export function hasSavedFilter(scope: string): boolean {
  const saved = readFilter(scope);
  return saved.language !== null || saved.country !== null;
}

export function TagFilterBar({ state, onChange }: { state: TagFilterState; onChange?: () => void }) {
  const { active, filtered, languages, countries } = state;
  if (!languages && !countries) return null;
  const change = (next: TagFilter) => {
    state.change(next);
    onChange?.();
  };
  return (
    <div className="browse-filters">
      {languages ? <FilterSelect label={t('filter.language')} anyLabel={t('filter.allLanguages')} value={active.language} options={languages} onChange={(language) => change({ ...active, language })} name={languageName} /> : null}
      {countries ? <FilterSelect label={t('filter.country')} anyLabel={t('filter.allCountries')} value={active.country} options={countries} onChange={(country) => change({ ...active, country })} /> : null}
      {filtered ? (
        <button type="button" className="browse-filters__clear" onClick={() => change(NO_FILTER)}>
          Clear
        </button>
      ) : null}
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  anyLabel: string;
  value: string | null;
  options: [string, number][];
  onChange: (value: string | null) => void;
  /** How an option is shown (languages are stored by English name). */
  name?: (value: string) => string;
}

function FilterSelect({ label, anyLabel, value, options: choices, onChange, name: show = (option) => option }: FilterSelectProps) {
  return (
    <select className="select browse-filters__select" aria-label={label} value={value ?? ''} onChange={(event) => onChange(event.target.value || null)}>
      <option value="">{anyLabel}</option>
      {choices.map(([name, count]) => (
        <option key={name} value={name}>
          {show(name)} ({count.toLocaleString()})
        </option>
      ))}
    </select>
  );
}

const FILTER_KEY = 'viva.browseFilter';

/** Storage can be unavailable (private mode, TV quirks). */
function readFilter(scope: string): TagFilter {
  try {
    const saved = JSON.parse(localStorage.getItem(`${FILTER_KEY}.${scope}`) ?? 'null') as Partial<TagFilter> | null;
    return { language: typeof saved?.language === 'string' ? saved.language : null, country: typeof saved?.country === 'string' ? saved.country : null };
  } catch {
    return NO_FILTER;
  }
}

function saveFilter(scope: string, filter: TagFilter): void {
  try {
    localStorage.setItem(`${FILTER_KEY}.${scope}`, JSON.stringify(filter));
  } catch {
    // Not remembering the filter only costs convenience.
  }
}
